import { baseURLV2 } from "../../axios";
import {
  convertToEnglishDigit,
  onUploadProgress,
} from "../../helpers/Functions";
import ApiService from "./ApiService";

class ProductApiService extends ApiService {
  /**
   *
   * @param {number} providerID
   * @returns {Promise}
   */
  async shopSettings(providerID) {
    try {
      const res = await this.axios.get(`providers/${providerID}/settings`, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} id shopId
   * @param {boolean} value 
   * @returns {Promise}
   */
  async storeOrEditShopSettings(
    id,
    value,
    providerId,
  ) {
    const formData = new FormData();

    if (typeof value === 'boolean') {
      formData.append("value", value ? 1 : 0);
    } else {
      formData.append("value", value);
    }

    try {
      const res = await this.axios.post(`providers/${providerId}/settings/${id}`, formData, { baseURL: baseURLV2 });
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @param {number} productID
   * @returns {Promise}
   */
  async deleteProduct(providerID, productID) {
    try {
      const res = await this.axios.delete(
        `providers/${providerID}/items/${productID}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @param {number} categoryID
   * @returns {Promise}
   */
  async deleteCategory(providerID, categoryID) {
    try {
      const res = await this.axios.delete(
        `providers/${providerID}/categories/${categoryID}`
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @param {number} productID
   * @param {JSON} body
   * @returns {Promise}
   */
  async createOrEditProduct(providerID, productID = null, body, updateProgress, abortController) {
    const formData = new FormData();

    if (body.english_title === null || body.english_title === undefined) {
      formData.append("english_title", '');
    } else {
      formData.append("english_title", body.english_title);
    }

    formData.append("available", body.available ? 1 : 0);
    formData.append("featured", body.featured ? 1 : 0);
    formData.append("details", JSON.stringify(JSON.stringify(body.details)));
    if (body.discount)
      formData.append("discount", convertToEnglishDigit(body.discount));

    if (body.album) {
      body.album.forEach((item) => {
        if (item instanceof File) {
          formData.append("album[]", item);
        }
      });
    }
    /**
     * Only the first image will upload as thumbnail
     */
    if (body.thumbnail) {
      body.thumbnail.forEach((item) => {
        if (item instanceof File) {
          formData.append("thumbnail[]", item);
        }
      });
    }

    if (body.level) formData.append("level", convertToEnglishDigit(body.level));
    formData.append(
      "price",
      body.price ? +convertToEnglishDigit(body.price.splitNumberFormat()) : 0
    );
    if (body.title) formData.append("title", body.title);
    if (body.unit) formData.append("unit", body.unit);
    if (body.subCategory) {
      formData.append("category_id", body.subCategory.id);
    } else if (body.category) {
      formData.append("category_id", body.category.id);
    }
    if (+body.discount > 0 && body.tag_id)
      formData.append("tag_id", body.tag_id);
    if (productID) formData.append("_method", "PATCH");

    let extraConfig = null;
    if (updateProgress || abortController) {
      extraConfig = {
        signal: abortController?.signal,
        onUploadProgress: (progressEvent) => {
          const result = onUploadProgress?.(progressEvent);
          updateProgress(result);
        },
      };
    }

    try {
      const res = await this.axios.post(
        `providers/${providerID}/items${productID ? `/${productID}` : ""}`,
        formData,
        {
          headers: {
            "content-type": "multipart/form-data",
          },
          ...extraConfig
        }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }

  /**
   *
   * @param {number} providerID
   * @param {number} categoryID
   * @param {JSON} body
   * @returns {Promise}
   */
  async createOrEditCategory(providerID, categoryID = null, body, parentId, updateProgress, abortController) {
    const formData = new FormData();

    if (body.visible === null || body.visible === undefined) {
      formData.append("visible", 1);
    } else {
      formData.append("visible", body.visible ? 1 : 0);
    }

    formData.append("available", body.available ? 1 : 0);
    if (body.title) formData.append("title", body.title);
    if (body.level) formData.append("level", convertToEnglishDigit(body.level));
    formData.append("description", body.description || "");

    if (body.thumbnail) {
      body.thumbnail.forEach(item => {
        if (item instanceof File) {
          formData.append("thumbnail[]", item);
        }
      });
    }

    if (parentId) formData.append("parent_id", parentId);
    if (categoryID) formData.append("_method", "PATCH");

    let extraConfig = null;
    if (updateProgress || abortController) {
      extraConfig = {
        signal: abortController?.signal,
        onUploadProgress: (progressEvent) => {
          const result = onUploadProgress?.(progressEvent);
          updateProgress(result);
        },
      };
    }

    try {
      const res = await this.axios.post(
        `providers/${providerID}/categories${categoryID ? `/${categoryID}` : ""
        }`,
        formData,
        {
          headers: {
            "content-type": "multipart/form-data",
          },
          ...extraConfig
        }
      );
      return res;
    } catch (err) {
      return this.throwFetchError(err);
    }
  }
}

const productApiService = new ProductApiService();
export default productApiService;
